import { useEffectEvent, useLayoutEffect, useRef } from 'react';

import { HEADER_HEIGHT_CSS_VAR } from '@/styles/theme/constants';

function readBlockSize(entry: ResizeObserverEntry): number {
  const box = entry.borderBoxSize[0];
  return Math.round(box?.blockSize ?? entry.contentRect.height);
}

function publishHeaderHeight(px: number) {
  const next = `${px}px`;
  const { documentElement } = document;
  if (documentElement.style.getPropertyValue(HEADER_HEIGHT_CSS_VAR) === next) {
    return;
  }
  documentElement.style.setProperty(HEADER_HEIGHT_CSS_VAR, next);
}

/**
 * Keeps `--header-height` on `:root` in sync with the page `<header>`.
 * Writes the DOM directly so measuring never re-renders React.
 */
export function useHeaderHeightCssVar() {
  const observedHeaderRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const syncHeader = useEffectEvent(() => {
    const header = document.querySelector('header');
    const observer = resizeObserverRef.current;
    const previous = observedHeaderRef.current;

    if (!(header instanceof HTMLElement)) {
      if (previous && observer) {
        observer.unobserve(previous);
      }
      observedHeaderRef.current = null;
      publishHeaderHeight(0);
      return;
    }

    if (previous === header) {
      return;
    }

    if (previous && observer) {
      observer.unobserve(previous);
    }

    observer?.observe(header);
    observedHeaderRef.current = header;
    publishHeaderHeight(Math.round(header.getBoundingClientRect().height));
  });

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      publishHeaderHeight(readBlockSize(entry));
    });
    resizeObserverRef.current = observer;

    return () => {
      observer.disconnect();
      resizeObserverRef.current = null;
      observedHeaderRef.current = null;
      document.documentElement.style.removeProperty(HEADER_HEIGHT_CSS_VAR);
    };
  }, []);

  useLayoutEffect(() => {
    syncHeader();
  });
}
